import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon } from '@heroicons/react/24/outline';
import PageContainer from '../components/layout/PageContainer';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface User {
  uid: string;
  name: string;
  balance: number;
  avatar: string;
}

function getInitials(name: string) {
  if (!name) return '';
  const names = name.trim().split(' ');
  if (names.length === 1) return names[0].slice(0, 2).toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
}

function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a pastel color
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 60%)`;
}

const Ranking: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRanking = async () => {
      try {
        console.log('Fetching ranking data...');
        const response = await fetch('https://getusersranking-b7jrylvg3a-uc.a.run.app', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });
        
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Received data:', data);
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received');
        }

        // Sort users by balance in descending order
        const sortedUsers = data.sort((a, b) => b.balance - a.balance);
        console.log('Sorted users:', sortedUsers);
        
        setUsers(sortedUsers);
        setError(null);
      } catch (error) {
        console.error('Error fetching ranking:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch ranking data');
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center min-h-screen">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center min-h-screen text-center p-4">
          <TrophyIcon className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Erro ao carregar ranking</h2>
          <p className="text-white/60">{error}</p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => window.location.reload()}
            className="mt-4 bg-primary text-white px-6 py-2 rounded-xl font-medium hover:bg-primary/90 transition-colors"
          >
            Tentar Novamente
          </motion.button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <TrophyIcon className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-white">Ranking de Pontos</h1>
        </div>

        {/* Ranking List */}
        <div className="space-y-3">
          {users.slice(0, 20).map((user, index) => (
            <motion.div
              key={user.uid}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/10 backdrop-blur-lg rounded-xl p-4"
            >
              <div className="flex items-center gap-4">
                {/* Position */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  index === 0 ? 'bg-yellow-500 text-black' :
                  index === 1 ? 'bg-gray-400 text-black' :
                  index === 2 ? 'bg-amber-600 text-white' :
                  'bg-white/20 text-white'
                }`}>
                  {index + 1}
                </div>

                {/* Profile Picture */}
                <div 
                  className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center text-white font-medium"
                  style={{ backgroundColor: stringToColor(user.name) }}
                >
                  {getInitials(user.name)}
                </div>

                {/* User Info */}
                <div className="flex-1">
                  <h3 className="text-white font-medium">
                    {user.name}
                  </h3>
                  <p className="text-white/60 text-sm">
                    {user.balance} pontos
                  </p>
                </div>

                {/* Medal for top 3 */}
                {index < 3 && (
                  <div className="text-2xl">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </PageContainer>
  );
};

export default Ranking; 